import React from 'react';
import { Participant, Track } from 'twilio-video';
import Publication from '../Publication/Publication';
import usePublications from '../../hooks/usePublications/usePublications';
import useVideoContext from '../../hooks/useVideoContext/useVideoContext';

interface ParticipantTracksProps {
  participant: Participant;
  disableAudio?: boolean;
  enableScreenShare?: boolean;
  videoPriority?: Track.Priority | null;
}

/*
 *  The object model for the Room object (found here: https://www.twilio.com/docs/video/migrating-1x-2x#object-model) shows
 *  that Participant objects have TrackPublications, and TrackPublication objects have Tracks.
 *
 *  The React components in this application follow the same pattern. This ParticipantTracks component renders Publications,
 *  and the Publication component renders Tracks.
 */

export default function ParticipantTracks({
  participant,
  disableAudio,
  enableScreenShare,
  videoPriority,
}: ParticipantTracksProps) {
  const { room } = useVideoContext();
  const publications = usePublications(participant);
  const isLocal = participant === room.localParticipant;

  let filteredPublications;

  if (enableScreenShare && publications.some((p) => p.trackName.includes('screen'))) {
    filteredPublications = publications.filter((p) => !p.trackName.includes('camera'));
  } else {
    filteredPublications = publications.filter((p) => !p.trackName.includes('screen'));
  }

  // During Twilio signaling reconnection the same participant can briefly hold two
  // camera publications. Rendering both produces a duplicate video feed, so we keep
  // only the first camera-video publication.
  let seenCameraVideo = false;
  filteredPublications = filteredPublications.filter((p) => {
    if (p.kind === 'video' && p.trackName.includes('camera')) {
      if (seenCameraVideo) return false;
      seenCameraVideo = true;
    }
    return true;
  });

  return (
    <>
      {filteredPublications.map((publication) => (
        <Publication
          key={publication.trackSid ?? `${publication.trackName}-${publication.kind}`}
          publication={publication}
          participant={participant}
          isLocal={isLocal}
          disableAudio={disableAudio}
          videoPriority={videoPriority}
        />
      ))}
    </>
  );
}
